export function hashKey(key: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = (h * 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

export type CookieKey = "clientId" | "externalUserId" | "accessToken" | "sessionData" | "metadataHash";

export interface CookieManagerOptions {
  readonly maxAgeSeconds?: number;
  readonly keys?: Partial<Record<CookieKey, string>>;
}

export class CookieManager {
  static readonly DEFAULT_MAX_AGE_SECONDS = 60 * 60 * 24 * 30; // 30 days
  static readonly DEFAULT_KEYS: Record<CookieKey, string> = {
    clientId: "client-id",
    externalUserId: "external-user-id",
    accessToken: "access-token",
    sessionData: "session-data",
    metadataHash: "metadata-hash",
  };

  constructor(namespace: string, options?: CookieManagerOptions) {
    this.namespace = CookieManager.sanitizeNamespace(namespace);
    this.maxAgeSeconds = options?.maxAgeSeconds ?? CookieManager.DEFAULT_MAX_AGE_SECONDS;
    this.keys = { ...CookieManager.DEFAULT_KEYS, ...(options?.keys ?? {}) };
  }

  private readonly namespace: string;
  private readonly maxAgeSeconds: number;
  private readonly keys: Record<CookieKey, string>;

  static buildNamespace(_baseUrl: string, apiKey: string): string {
    return `sophi-${hashKey(apiKey)}`;
  }

  static canUseCookies(): boolean {
    return typeof document !== "undefined" && typeof document.cookie === "string";
  }

  set(key: CookieKey, value: string): void {
    if (!CookieManager.canUseCookies()) {
      return;
    }

    document.cookie = `${this.formatName(key)}=${encodeURIComponent(value)}; path=/; max-age=${this.maxAgeSeconds}; samesite=lax`;
  }

  get(key: CookieKey): string | null {
    if (!CookieManager.canUseCookies()) {
      return null;
    }

    return CookieManager.readRawCookie(this.formatName(key));
  }

  delete(key: CookieKey): void {
    if (!CookieManager.canUseCookies()) {
      return;
    }

    document.cookie = `${this.formatName(key)}=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; samesite=lax`;
  }

  clearAll(): void {
    Object.keys(this.keys).forEach((key) => {
      this.delete(key as CookieKey);
    });
  }

  private formatName(key: CookieKey): string {
    return `${this.namespace}-${this.keys[key]}`;
  }

  private static readRawCookie(name: string): string | null {
    const cookies = document.cookie.split(";").map((cookie) => cookie.trim());
    for (const cookie of cookies) {
      if (!cookie) {
        continue;
      }
      const [cookieName, ...cookieValueParts] = cookie.split("=");
      if (cookieName === name) {
        return decodeURIComponent(cookieValueParts.join("="));
      }
    }
    return null;
  }

  private static sanitizeNamespace(value: string): string {
    const sanitized = value.replace(/[^a-zA-Z0-9_-]/g, "");
    return sanitized.length ? sanitized : "sophi-widget";
  }
}
